import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonTitle,
    IonToolbar,
    useIonRouter
} from "@ionic/react";
import {
    addOutline,
    checkmarkCircleOutline,
    closeOutline,
    helpCircleOutline,
    peopleOutline,
    personRemoveOutline,
    refreshOutline,
    settingsOutline,
    shirtOutline,
    swapHorizontalOutline,
    trophyOutline
} from "ionicons/icons";
import { useState } from "react";
import {
    useLocation
} from "react-router-dom";

import "./FantasyHeaderActions.css";

export type FantasyHelpScreen =
    | "league"
    | "players"
    | "dropPlayers"
    | "topPlayers"
    | "team"
    | "game"
    | "draft";

interface FantasyHeaderActionsProps {
    leagueId: string;
    screen: FantasyHelpScreen;
    showSettings?: boolean;
}

interface FantasyHelpContent {
    title: string;
    description: string;
    actions: Array<{
        icon: string;
        title: string;
        description: string;
    }>;
    showPositions?: boolean;
}

const helpContent: Record<
    FantasyHelpScreen,
    FantasyHelpContent
> = {
    league: {
        title: "Ayuda de la liga",
        description:
            "Consulta el estado general de tu liga, calendario, posiciones y movimientos recientes.",
        actions: [
            {
                icon: trophyOutline,
                title: "Juegos de la semana",
                description:
                    "Usa las flechas para consultar jornadas anteriores o siguientes. Toca “Ver puntos de jugadores” para revisar el aporte individual."
            },
            {
                icon: trophyOutline,
                title: "Tabla de posiciones",
                description:
                    "Muestra victorias, derrotas, puntos a favor y puntos en contra de cada equipo."
            },
            {
                icon: refreshOutline,
                title: "Actualizar información",
                description:
                    "Desliza hacia abajo para actualizar la liga, resultados, clasificación y actividad."
            },
            {
                icon: settingsOutline,
                title: "Configuración",
                description:
                    "Accede a reglas, ajustes, draft y opciones administrativas disponibles para la liga."
            }
        ]
    },

    players: {
        title: "Ayuda de jugadores",
        description:
            "Aquí puedes buscar agentes libres, filtrarlos por posición y agregarlos a tu roster.",
        actions: [
            {
                icon: addOutline,
                title: "Agregar o reclamar",
                description:
                    "El botón Agregar incorpora un agente libre a tu equipo. Si el jugador está sujeto a waivers, el botón mostrará Reclamar."
            },
            {
                icon: peopleOutline,
                title: "Ficha del jugador",
                description:
                    "Toca una tarjeta para consultar estadísticas, proyecciones, rival, historial y acciones disponibles."
            },
            {
                icon: checkmarkCircleOutline,
                title: "Waivers",
                description:
                    "Los reclamos pendientes se procesan según la prioridad de waivers de tu equipo. Puedes cancelar un reclamo mientras siga pendiente."
            },
            {
                icon: refreshOutline,
                title: "Filtros y actualización",
                description:
                    "Usa los filtros por posición y desliza hacia abajo para actualizar la agencia libre."
            }
        ],
        showPositions: true
    },

    dropPlayers: {
        title: "Ayuda para soltar jugadores",
        description:
            "Administra los jugadores de tu roster que deseas liberar.",
        actions: [
            {
                icon: personRemoveOutline,
                title: "Soltar jugador",
                description:
                    "Presiona Soltar y confirma la acción. El jugador quedará disponible para los demás equipos de la liga."
            },
            {
                icon: peopleOutline,
                title: "Ficha del jugador",
                description:
                    "Toca la tarjeta de un jugador para abrir su perfil, consultar sus proyecciones y revisar sus estadísticas."
            },
            {
                icon: checkmarkCircleOutline,
                title: "Jugadores bloqueados",
                description:
                    "No puedes liberar a un jugador cuando su partido ya inició. La tarjeta mostrará el motivo del bloqueo."
            },
            {
                icon: refreshOutline,
                title: "Actualizar roster",
                description:
                    "Desliza hacia abajo para sincronizar tu roster y el estado de bloqueo de cada jugador."
            }
        ],
        showPositions: true
    },

    topPlayers: {
        title: "Ayuda de líderes",
        description:
            "Compara el rendimiento de todos los jugadores de la liga y consulta su disponibilidad.",
        actions: [
            {
                icon: trophyOutline,
                title: "Ordenar ranking",
                description:
                    "Alterna entre Temporada y Jornada para ordenar a los jugadores por sus puntos acumulados o puntos semanales."
            },
            {
                icon: peopleOutline,
                title: "Ver jugador",
                description:
                    "Toca una tarjeta para abrir la ficha completa del jugador."
            },
            {
                icon: addOutline,
                title: "Jugador disponible",
                description:
                    "Si es agente libre, podrás reclamarlo o ir a la pantalla de jugadores para agregarlo."
            },
            {
                icon: swapHorizontalOutline,
                title: "Intercambio",
                description:
                    "Si el jugador pertenece a otro equipo, desde su ficha podrás enviar una propuesta de intercambio."
            }
        ],
        showPositions: true
    },

    team: {
        title: "Ayuda de mi equipo",
        description:
            "Gestiona tu alineación, consulta puntos, proyecciones, bloqueos e intercambios.",
        actions: [
            {
                icon: shirtOutline,
                title: "Cambiar alineación",
                description:
                    "Toca la etiqueta de posición de un titular para elegir un jugador elegible de la banca."
            },
            {
                icon: peopleOutline,
                title: "Ver ficha",
                description:
                    "Toca un jugador para consultar sus estadísticas, proyecciones, rival y acciones disponibles."
            },
            {
                icon: checkmarkCircleOutline,
                title: "Bloqueos",
                description:
                    "Un jugador no puede moverse, intercambiarse ni liberarse después de iniciar su partido."
            },
            {
                icon: swapHorizontalOutline,
                title: "Intercambios",
                description:
                    "Revisa propuestas recibidas o enviadas. Puedes aceptar o cancelar según corresponda."
            }
        ],
        showPositions: true
    },

    game: {
        title: "Ayuda del enfrentamiento",
        description:
            "Consulta tu duelo Fantasy, el marcador, proyecciones, alineaciones y calendario real.",
        actions: [
            {
                icon: refreshOutline,
                title: "Actualizar",
                description:
                    "Usa el botón de actualización para consultar los puntos y el estado más reciente de la jornada."
            },
            {
                icon: trophyOutline,
                title: "Marcador",
                description:
                    "El marcador considera únicamente a los titulares. Los jugadores de banca no suman puntos al enfrentamiento."
            },
            {
                icon: peopleOutline,
                title: "Alineaciones",
                description:
                    "Revisa titulares y banca de ambos equipos, incluyendo puntos reales y proyecciones iniciales."
            },
            {
                icon: checkmarkCircleOutline,
                title: "Calendario real",
                description:
                    "Abre el calendario para consultar los partidos reales de la jornada y toca uno para ver su detalle."
            }
        ]
    },

    draft: {
        title: "Ayuda del draft",
        description:
            "Selecciona jugadores para construir tu roster antes de que termine el tiempo de tu turno.",
        actions: [
            {
                icon: trophyOutline,
                title: "Turno y reloj",
                description:
                    "Cuando aparezca “Tu turno”, selecciona un jugador antes de que termine el contador."
            },
            {
                icon: peopleOutline,
                title: "Buscar y filtrar",
                description:
                    "Busca por nombre o equipo, y usa los filtros de posición para encontrar jugadores disponibles."
            },
            {
                icon: checkmarkCircleOutline,
                title: "Elegir jugador",
                description:
                    "El botón Elegir sólo se activa durante tu turno. Una selección no puede deshacerse."
            },
            {
                icon: shirtOutline,
                title: "Mi roster e historial",
                description:
                    "Abre los acordeones para consultar tus selecciones actuales y el historial completo del draft."
            }
        ],
        showPositions: true
    }
};

function PositionGuide(): JSX.Element {
    const positions = [
        ["QB", "Quarterback", "Mariscal de campo."],
        ["RB", "Running Back", "Corredor."],
        ["WR", "Wide Receiver", "Receptor."],
        ["FLEX", "Flexible", "Puede ocupar un RB o WR."],
        ["K", "Kicker", "Pateador."],
        ["DEF", "Defensiva", "Defensa del equipo."],
        ["BN", "Banca", "No suma al marcador semanal."]
    ];

    return (
        <section className="fantasy-help__positions">
            <span className="fantasy-help__eyebrow">
                NOMENCLATURA
            </span>

            <h3>Posiciones Fantasy</h3>

            <div className="fantasy-help__positions-grid">
                {positions.map(function ([code, title, description]) {
                    return (
                        <article key={code}>
                            <strong>{code}</strong>

                            <div>
                                <b>{title}</b>
                                <span>{description}</span>
                            </div>
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

const FantasyHeaderActions: React.FC<
    FantasyHeaderActionsProps
> = function ({
    leagueId,
    screen,
    showSettings = true
}) {
    const router = useIonRouter();
    const location = useLocation();
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const content = helpContent[screen];

    const currentPath =
    location.pathname + location.search;

const settingsHref =
    `/fantasySettingsMenu/${leagueId}` +
    `?from=${encodeURIComponent(currentPath)}`;

    return (
        <>
            <IonButtons slot="end">
                <IonButton
                    fill="clear"
                    aria-label={`Abrir ayuda de ${content.title}`}
                    onClick={function () {
                        setIsHelpOpen(true);
                    }}
                >
                    <IonIcon icon={helpCircleOutline} />
                </IonButton>

                {showSettings && (
                    <IonButton
                        fill="clear"
                        aria-label="Configuración de liga"
onClick={function (event) {
    event.preventDefault();
    event.stopPropagation();

    console.log("[FantasyHeaderActions] abrir Settings", {
        current: location.pathname + location.search,
        settingsHref
    });

    router.push(
        settingsHref,
        "forward",
        "push"
    );
}}
                    >
                        <IonIcon icon={settingsOutline} />
                    </IonButton>
                )}
            </IonButtons>

            <IonModal
                className="fantasy-help-modal"
                isOpen={isHelpOpen}
                onDidDismiss={function () {
                    setIsHelpOpen(false);
                }}
            >
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>{content.title}</IonTitle>

                        <IonButtons slot="end">
                            <IonButton
                                fill="clear"
                                aria-label="Cerrar ayuda"
                                onClick={function () {
                                    setIsHelpOpen(false);
                                }}
                            >
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="fantasy-help-modal__content">
                    <main className="fantasy-help">
                        <section className="fantasy-help__hero">
                            <span className="fantasy-help__hero-icon">
                                <IonIcon icon={helpCircleOutline} />
                            </span>

                            <div>
                                <span>GUÍA DE PANTALLA</span>
                                <h1>{content.title}</h1>
                                <p>{content.description}</p>
                            </div>
                        </section>

                        <section className="fantasy-help__actions">
                            <span className="fantasy-help__eyebrow">
                                ¿QUÉ PUEDES HACER?
                            </span>

                            {content.actions.map(function (action) {
                                return (
                                    <article
                                        className="fantasy-help__action"
                                        key={action.title}
                                    >
                                        <span>
                                            <IonIcon icon={action.icon} />
                                        </span>

                                        <div>
                                            <strong>{action.title}</strong>
                                            <p>{action.description}</p>
                                        </div>
                                    </article>
                                );
                            })}
                        </section>

                        {content.showPositions && (
                            <PositionGuide />
                        )}

                        <IonButton
                            expand="block"
                            onClick={function () {
                                setIsHelpOpen(false);
                            }}
                        >
                            Entendido
                        </IonButton>
                    </main>
                </IonContent>
            </IonModal>
        </>
    );
};

export default FantasyHeaderActions;